import { AxiosError } from 'axios';
import { Form, Formik } from 'formik';
import { useRef } from 'react';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { IoClose } from 'react-icons/io5';

import cn from '@/lib/clsxm';
import { APIErrorResponseGenerator } from '@/lib/helper';

import Button from '@/components/@base/button';
import { BUTTON_TYPE } from '@/components/@base/button/type';
import Container from '@/components/@base/container';
import { Textarea } from '@/components/@base/input';
import Group from '@/components/@base/input/@shared/group';
import Label from '@/components/@base/input/@shared/label';
import CheckBox from '@/components/@base/input/check-box';
import { showToast } from '@/components/@base/toast/show-toast';
import { Heading, Text } from '@/components/@base/typography';
import { HEADING_TYPE } from '@/components/@base/typography/heading/type';
import XImage from '@/components/@base/x-image';
import Props from '@/components/app/demo/components/feedback-sidebar/type';
import { usePostFeedback } from '@/components/app/demo/services/hooks';
import Captcha from '@/components/shared/components/recaptcha';

import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';

const { demo: demoLocalization } = pageLevelLocalization;

export default function FeedbackSidebar({
  title,
  isOpen,
  toggleSidebar,
  productId,
  className,
}: Props) {
  const captchaRef = useRef<ReCAPTCHA>(null);
  const { mutate: postFeedback, isPending: isPostFeedbackPending } = usePostFeedback();

  const handleSubmit = (values) => {
    postFeedback(
      {
        product_id: productId,
        recaptcha: values.recaptcha,
        problem_preview: values.problemWithPreview,
        is_helpful: values.yesSelected,
        description: values.description,
      },
      {
        onSuccess: () => {
          showToast({ message: demoLocalization.feedbackSavedSuccessfully, type: 'success' });
          if (captchaRef.current) {
            captchaRef.current.reset();
          }
          toggleSidebar();
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            showToast(APIErrorResponseGenerator(error));
          }
        },
      },
    );
  };

  return (
    <Container
      className={cn(
        'fixed bottom-0 right-0 z-50 w-full max-w-[366px] transform bg-white shadow-[0px_40px_40px_-0px_#00000026] transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-x-0' : 'translate-x-full',
        className,
      )}
    >
      <Container className='flex h-full flex-col'>
        <Container
          center
          className='mx-[15px] justify-between gap-3.5 border-b-[1px] border-b-[#FFE6C0] py-[15px]'
        >
          <Container center className='gap-3.5'>
            <XImage src={imagesList.icons.bellIcon} alt='feedback' width={16} height={17.8} />
            <Heading
              type={HEADING_TYPE.H5}
              className='text-[#424244]'
            >{`${demoLocalization.demoReport} ${title}`}</Heading>
          </Container>

          <Container
            center
            className='h-[50px] w-[50px] shrink-0 cursor-pointer rounded-lg bg-[#F9FAFC] transition duration-300 hover:bg-[#E5E8EB]'
            onClick={toggleSidebar}
          >
            <IoClose size={14} fill='#878F9B' />
          </Container>
        </Container>

        <Formik
          initialValues={{
            problemWithPreview: false,
            yesSelected: false,
            noSelected: false,
            recaptcha: null,
            description: '',
          }}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, setFieldValue }) => {
            const isSubmitEnabled =
              values.recaptcha &&
              (values.problemWithPreview || values.yesSelected || values.noSelected);

            const handleOptionChange = (option) => {
              if (option === 'yes') {
                setFieldValue('yesSelected', !values.yesSelected);
                setFieldValue('noSelected', false);
              } else {
                setFieldValue('noSelected', !values.noSelected);
                setFieldValue('yesSelected', false);
              }
            };

            return (
              <Form className='flex grow flex-col justify-between'>
                <Container className='p-3.5'>
                  <Group className='flex w-full items-center gap-2 py-4'>
                    <CheckBox
                      name='problemWithPreview'
                      checked={values.problemWithPreview}
                      onChange={() =>
                        setFieldValue('problemWithPreview', !values.problemWithPreview)
                      }
                      className='bg-[#E5E8EB]'
                    />
                    <Label className='flex-1 text-sm text-[#5B5C60]'>
                      {demoLocalization.thereIsAProblemWithPreview}
                    </Label>
                  </Group>

                  <Text>{demoLocalization.wasItUseful}</Text>

                  <Group className='flex w-full items-center gap-2 py-4'>
                    <CheckBox
                      name='yesSelected'
                      checked={values.yesSelected}
                      onChange={() => handleOptionChange('yes')}
                      className='bg-[#E5E8EB]'
                    />
                    <Label className='flex-1 text-sm text-[#5B5C60]'>
                      {demoLocalization.yes}
                    </Label>
                  </Group>

                  <Group className='flex w-full items-center gap-2 py-4'>
                    <CheckBox
                      name='noSelected'
                      checked={values.noSelected}
                      onChange={() => handleOptionChange('no')}
                      className='bg-[#E5E8EB]'
                    />
                    <Label className='flex-1 text-sm text-[#5B5C60]'>
                      {demoLocalization.no}
                    </Label>
                  </Group>

                  {values.noSelected && (
                    <Textarea
                      name='description'
                      placeholder={demoLocalization.optionalDescription}
                      value={values.description}
                      onChange={handleChange}
                      className='h-[99px] w-full rounded-md border-[1px] border-[#E5E8EB] p-2 shadow-[0px_2px_6px_0px_#8A9DB912] focus:outline-hidden'
                    />
                  )}
                </Container>

                <Container center className='flex-col'>
                  <Captcha
                    onChange={setFieldValue}
                    ref={captchaRef}
                    className='my-2 md:my-4 [@media(max-height:730px)]:my-2'
                  />
                  <Container className='w-full bg-[#F9FAFC] p-[15px]'>
                    <Button
                      className={cn(
                        'h-[46px] w-full rounded-md border-[1px] border-[#E5E8EB] bg-[#FFAE11] shadow-[0px_5px_15px_0px_#494B540F] hover:bg-[#EB8800]',
                        !isSubmitEnabled && 'bg-[#FFFFFF]! text-[#878F9B]!',
                      )}
                      type={BUTTON_TYPE.SUBMIT}
                      disabled={!isSubmitEnabled}
                      isLoading={isPostFeedbackPending}
                    >
                      {demoLocalization.saveReport}
                    </Button>
                  </Container>
                </Container>
              </Form>
            );
          }}
        </Formik>
      </Container>

      <Button
        className='group absolute left-[-84px] top-[50%] flex h-[35px] w-[133px] -translate-y-1/2 -rotate-90 transform items-center justify-center rounded-b-none rounded-t-lg bg-[#FFF5DE] p-2 transition duration-300 hover:bg-[#FFB616]'
        onClick={toggleSidebar}
      >
        <Text className='text-[13px] text-[#FFB616] group-hover:text-[#FFFFFF]'>
          {demoLocalization.giveDemoFeedback}
        </Text>
      </Button>
    </Container>
  );
}
